import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useSearch } from 'wouter';
import { PageSkeleton } from '@/components/PageSkeleton';
import { useConsentState } from '@/features/consent/hooks/useConsentState';
import { useConsentText } from '@/features/consent/hooks/useConsentText';

const CONSENT_PATH = '/consent/electronic_records';

// The capture itself, the token-bearing routes (bouncing them would drop the
// ?token) and the unit pickers, which the unit guards send to on their own.
const EXEMPT_PATHS = new Set([CONSENT_PATH, '/reset-password', '/verify-email-change', '/unit-select', '/owner/unit-select']);

// EX-01: a signing screen asks for the consent right there — the ballot does it
// with its own capture — so the member keeps his place instead of a detour.
const SIGNING_PREFIXES = ['/owner/votes/'];

function isExempt(location: string): boolean {
  return EXEMPT_PATHS.has(location) || SIGNING_PREFIXES.some((prefix) => location.startsWith(prefix));
}

// The "Not now" of the capture, set only while the guard is asking.
const NotNowContext = createContext<(() => void) | null>(null);

/** The capture offers "Not now" when this is set: only to a resident the guard is asking. */
export function useElectronicRecordsConsentNotNow(): (() => void) | null {
  return useContext(NotNowContext);
}

// N08 (868mn3t52): the page the guard stopped, so accepting goes back there.
const ReturnPathContext = createContext<string | null>(null);

/** Where the capture sends the resident after accepting: the page asked for, if the guard stopped one. */
export function useElectronicRecordsConsentReturnPath(): string | null {
  return useContext(ReturnPathContext);
}

/**
 * ElectronicRecordsConsentGuard — ESIGN-01 CA-01 (868kmgf8e)
 *
 * Asks the owner and the tenant for the electronic records consent once, when
 * the account is activated: until they answer, the portal sends them to the
 * generic capture of CONSENT-01. Only "never answered" sends them there. A
 * revocation is an answer and keeps the portal readable (CA-04), and a grant
 * of an older version stays valid (EX-03), so neither brings the screen back.
 *
 * "Not now" (PO, 14-oct-2026) lets them in without the consent, the same as a
 * revocation: the portal reads in full and the signing points stay blocked
 * with the paper channel message (CA-05). Nothing is recorded: it lasts while
 * the guard is mounted, so the next sign-in asks again.
 */
export function ElectronicRecordsConsentGuard({ children }: { children: React.ReactNode }) {
  const [location, setLocation] = useLocation();
  const search = useSearch();
  const state = useConsentState('electronic_records');
  const text = useConsentText('electronic_records');
  const [notNow, setNotNow] = useState(false);
  // A ref and not state: keeping it must not render the portal again under the
  // redirect; the router's own render after the redirect hands it down.
  const returnPath = useRef<string | null>(null);
  const skip = useCallback(() => setNotNow(true), []);

  const neverAnswered = state.data?.latest === null && !notNow;
  // A failed request, or no published text, leaves this false on purpose: an
  // unreachable consent API must not lock every resident out of the portal.
  const asking = neverAnswered && text.isSuccess;
  const mustRedirect = asking && !isExempt(location);
  // N09 (868mn3t52): with the text down nothing redirects, but a resident who
  // reloads the capture still gets "Not now" next to Retry, not a dead end.
  const offerNotNow = neverAnswered && (text.isSuccess || text.isError);

  // The skeleton covers the first answer only. Without data, TanStack puts a
  // refetch of a failed query back in `pending`: a screen under the guard that
  // reads the same query (the profile card) refetches it when it mounts, and
  // swapping the portal for the skeleton then unmounted it and mounted it
  // again on every failure, a request loop with a blank screen.
  const stateLoading = state.isLoading && !state.isFetched;
  const textLoading = text.isLoading && !text.isFetched;

  useEffect(() => {
    if (mustRedirect) {
      // The query too: `location` is the path alone, and «?tab=payments» is
      // part of the page that was asked for.
      returnPath.current = search ? `${location}?${search}` : location;
      setLocation(CONSENT_PATH);
    }
  }, [mustRedirect, location, search, setLocation]);

  if (stateLoading || (neverAnswered && textLoading && !isExempt(location)) || mustRedirect) {
    return <PageSkeleton />;
  }

  return (
    <NotNowContext.Provider value={offerNotNow ? skip : null}>
      <ReturnPathContext.Provider value={returnPath.current}>{children}</ReturnPathContext.Provider>
    </NotNowContext.Provider>
  );
}
